// Qordo Catalog — Product Data
// For each product we answer 5 strict questions:
//   1. Why does this industry need this app?
//   2. What does it solve for them (pain points)?
//   3. Does it save money or make them money (value)?
//   4. Why would they pay (realistic paid reason)?
//   5. What features does it include?
//
// Pricing model is identical across all products (keeps referrer pitch simple):
//   - Setup fee:  €30 (one-time, paid by business owner, goes to referrer as instant commission)
//   - Starter:    €29 / month
//   - Pro:        €59 / month
//   - Business:   €99 / month
// Referrer earns: 30% of monthly subscription for 6 months + the full €30 setup fee.

export type Category = {
  slug: string
  name: string
  icon: string
  color: string
  blurb: string
}

export type Product = {
  slug: string
  name: string
  category: string // category slug
  tagline: string
  icon: string
  demoUrl: string | null // null = "coming soon, contact for preview"
  /** Real self-serve products only: direct signup URL ("try it, buy it, done").
   *  Partner links append ?ref=<code> for the 60-day-trial attribution. */
  signupUrl?: string
  // Strict framing:
  whyNeed: string // why the business needs it
  whatSolves: string[] // concrete pain points removed
  moneyImpact: string // saves OR makes them money — with rough numbers
  whyPay: string // realistic paid reason
  features: string[]
  plans: {
    starter: { price: number; includes: string[] }
    pro: { price: number; includes: string[] }
    business: { price: number; includes: string[] }
  }
}

export const CATEGORIES: Category[] = [
  {
    slug: 'booking',
    name: 'Booking & Appointments',
    icon: '📅',
    color: 'from-indigo-500 to-purple-600',
    blurb: 'Online scheduling for service businesses that live or die by their calendar.',
  },
  {
    slug: 'rental',
    name: 'Rental Systems',
    icon: '🚗',
    color: 'from-sky-500 to-blue-600',
    blurb: 'Fleet / property / equipment rental with availability, pricing, contracts.',
  },
  {
    slug: 'hospitality',
    name: 'Food & Hospitality',
    icon: '🍕',
    color: 'from-orange-500 to-red-600',
    blurb: 'Ordering, reservations, delivery, and loyalty for cafes, restaurants, bars.',
  },
  {
    slug: 'trades',
    name: 'Trades & Home Services',
    icon: '🔧',
    color: 'from-amber-500 to-yellow-600',
    blurb: 'Quote requests and job tracking for plumbers, electricians, renovators.',
  },
  {
    slug: 'voice',
    name: 'AI Voice Agent',
    icon: '🤖',
    color: 'from-fuchsia-500 to-pink-600',
    blurb: '24/7 AI receptionist that answers calls, books appointments, never sleeps.',
  },
  {
    slug: 'loyalty',
    name: 'Loyalty & Gamification',
    icon: '🎰',
    color: 'from-rose-500 to-pink-600',
    blurb: 'Scratch cards, spin wheels, stamp cards — turn one-time buyers into regulars.',
  },
  {
    slug: 'ecommerce',
    name: 'E-commerce & Catalog',
    icon: '🛍',
    color: 'from-emerald-500 to-teal-600',
    blurb: 'Online store with product catalog, payments, local delivery.',
  },
  {
    slug: 'professional',
    name: 'Professional Services',
    icon: '💼',
    color: 'from-slate-500 to-slate-700',
    blurb: 'Landing pages + lead capture for lawyers, accountants, consultants.',
  },
  {
    slug: 'custom',
    name: 'Custom Build',
    icon: '✨',
    color: 'from-violet-500 to-purple-700',
    blurb: "Don't see your business? Request a custom build. We'll quote in 24 hours.",
  },
]

// Standard plan generator — keeps pricing consistent across the catalog
const standardPlans = (a: string[], b: string[], c: string[]) => ({
  starter: { price: 29, includes: a },
  pro: { price: 59, includes: b },
  business: { price: 99, includes: c },
})

export const PRODUCTS: Product[] = [
  // ──────────────────────────── CATEGORY 1 — BOOKING ────────────────────────────
  {
    slug: 'barbershop',
    name: 'Barbershop Booking',
    category: 'booking',
    tagline: 'Fill every chair. Stop playing phone-tag with clients.',
    icon: '💈',
    demoUrl: '/demo/barbershop',
    whyNeed:
      'Most barbershops still book by phone or Instagram DM. That means missed calls during haircuts = missed revenue, and double-bookings when two barbers answer the same DM.',
    whatSolves: [
      'Missed calls while cutting = lost bookings',
      'Double-booking across barbers',
      'No-shows with zero penalty',
      'No record of customer history (usual cut, preferred barber)',
    ],
    moneyImpact:
      'Average shop misses 15–20 calls/week. At €15/cut, that is €900–€1,200/month of walked-away revenue. Even recovering half of that pays for the system 20x over.',
    whyPay:
      'They pay because every missed call is money on the floor. €29/mo is cheaper than one missed haircut per week.',
    features: [
      'Customer picks barber + time online (24/7)',
      'SMS reminder 24h before (kills no-shows)',
      'Auto-rebook "usual cut" in 2 taps',
      'Barber dashboard: daily schedule, tips tracker',
      'Google Maps / Instagram booking button',
    ],
    plans: standardPlans(
      ['1 barber', 'Online booking', 'SMS reminders'],
      ['Up to 5 barbers', 'Customer history', 'No-show deposits'],
      ['Unlimited staff', 'Multi-location', 'Instagram + Google auto-booking'],
    ),
  },
  {
    slug: 'hair-salon',
    name: 'StyleStudio — Hair Salon Booking',
    category: 'booking',
    tagline: 'Self-serve booking pages for hair salons: chat-style scheduling, photo-attach for exact haircut requests, zero back-and-forth texting.',
    icon: '💇',
    demoUrl: 'https://style.myclienta.com/demo',
    signupUrl: 'https://style.myclienta.com/signup',
    whyNeed:
      'Most salons still book by phone, Instagram DM, or WhatsApp — that means missed calls mid-cut and hours spent typing "what time works for you?" every day.',
    whatSolves: [
      'Endless back-and-forth texting to lock in a time',
      'Clients describing a haircut in words instead of showing a photo',
      'Missed calls during a cut = lost bookings',
      'No branded booking page of their own — just a phone number',
    ],
    moneyImpact:
      'A no-show or a missed call is money off the chair. Automatic confirmations plus a booking link that works 24/7 turns idle DM time back into billable chair time.',
    whyPay:
      'Live product with a self-serve 30-day free trial — a salon owner can be live with their own booking page in under 2 minutes. Try the customer view live: style.myclienta.com/booking?biz=qa-test-salon · Pitch page: style.myclienta.com/pitch · Sign-up: style.myclienta.com/signup',
    features: [
      'Chat-style booking — service, time, done in under a minute',
      'Reference-photo attach for haircuts, so the stylist knows the look in advance',
      'Owner dashboard — bookings, services, prices in one place',
      'Self-serve signup, 30-day free trial',
    ],
    plans: standardPlans(
      ['1 salon', 'Online booking page'],
      ['+ Photo-ref bookings', '+ Owner dashboard'],
      ['Multi-location', 'White-label', 'Custom domain'],
    ),
  },
  {
    slug: 'nail-salon',
    name: 'Nail Salon Booking',
    category: 'booking',
    tagline: 'Book a gel set in 3 taps. Rebook automatically every 3 weeks.',
    icon: '💅',
    demoUrl: '/demo/nail-salon',
    whyNeed:
      'Nail services are highly repeat-based (every 2–3 weeks). Without an automated rebook, shops lose clients to whoever reminds them first.',
    whatSolves: [
      'Forgotten rebook = client goes elsewhere',
      'Phone-only booking locks out after-hours',
      'No gel/acrylic/pedicure add-on tracking',
    ],
    moneyImpact:
      'Retention uplift of 15–25% is typical. One regular client is worth €600–€900/year. 3 extra retained clients = €2,000+ annually.',
    whyPay:
      'Auto-rebook SMS alone is worth the subscription. "Hi Maria, your nails are 19 days old — book now?" wins.',
    features: [
      'Auto-rebook SMS (3 weeks after last visit)',
      'Color-pattern library (customer reference)',
      'Add-on upsell (pedicure, art, repair)',
      'Loyalty: 10th visit free',
    ],
    plans: standardPlans(
      ['1 chair', 'Online booking', 'Auto-rebook SMS'],
      ['3 chairs', 'Color library', 'Loyalty program'],
      ['Unlimited', 'Multi-location', 'Staff commission tracking'],
    ),
  },
  {
    slug: 'beauty-spa',
    name: 'Beauty Spa Booking',
    category: 'booking',
    tagline: 'Package deals, memberships, gift cards — all running automatically.',
    icon: '🧖',
    demoUrl: '/demo/beauty-spa',
    whyNeed:
      'Spas sell high-margin packages (10-session bundles, memberships). Tracking them on paper is a nightmare and leaks revenue.',
    whatSolves: [
      'Lost tracking of package sessions ("did she use 5 or 6?")',
      'No way to sell gift cards online',
      'No membership auto-renewal',
    ],
    moneyImpact:
      'Upselling packages lifts average ticket 40–60%. On €50 visits, that is €20–€30 extra per client.',
    whyPay: 'Package tracking + gift-card-for-Christmas revenue easily 10x the subscription.',
    features: [
      'Package & bundle tracking',
      'Online gift card shop',
      'Membership recurring billing',
      'Therapist commission split',
    ],
    plans: standardPlans(
      ['Single location', 'Online booking'],
      ['Packages + gift cards', 'Memberships'],
      ['Multi-location', 'Custom branding', 'POS integration'],
    ),
  },
  {
    slug: 'tattoo-studio',
    name: 'Tattoo Studio Booking',
    category: 'booking',
    tagline: 'Deposits required. Consultations scheduled. No more ghosted appointments.',
    icon: '🎨',
    demoUrl: '/demo/tattoo-studio',
    whyNeed:
      'Tattoo artists lose entire days to no-shows. Without a deposit system, flaky clients cost thousands.',
    whatSolves: [
      'No-show on a 6-hour slot = €400–€800 gone',
      'Impossible to schedule consultations separately from ink sessions',
      'No intake form = wasted session time on "what do you want?"',
    ],
    moneyImpact:
      'One saved no-show per month = €500+. Over a year that is €6,000 — roughly 12x the subscription cost.',
    whyPay:
      'Deposits are the whole reason tattoo shops sign up. They pay for themselves after one enforcement.',
    features: [
      'Non-refundable deposit required to book',
      'Reference image upload on booking',
      'Consultation vs session slot types',
      'Artist portfolio page per booking',
    ],
    plans: standardPlans(
      ['1 artist', 'Deposit booking'],
      ['Up to 4 artists', 'Portfolio pages', 'Intake forms'],
      ['Unlimited', 'Multi-studio', 'Custom branding'],
    ),
  },
  {
    slug: 'dentist',
    name: 'Dentist Clinic Booking',
    category: 'booking',
    tagline: 'Reminder SMS cut no-shows by 40%. Instant ROI.',
    icon: '🦷',
    demoUrl: '/demo/dentist',
    whyNeed:
      'Dentist no-shows are brutal — the chair, assistant, and hygienist are all paid to sit idle for an hour.',
    whatSolves: [
      'Hourly chair cost is €80–€120, burned on no-shows',
      'Recalls ("time for a cleaning") done manually or not at all',
      'Paper intake forms slow check-in',
    ],
    moneyImpact:
      'Reminder SMS alone cuts no-shows by ~40%. For a 4-chair clinic that is €2,000–€4,000/month preserved.',
    whyPay:
      'Clinics pay because one recovered no-show per week pays for it. Plus recall automation generates €1,000s in cleaning revenue.',
    features: [
      '6-month recall automation',
      'Digital intake forms',
      'Insurance eligibility capture',
      'Hygienist vs dentist routing',
    ],
    plans: standardPlans(
      ['1 dentist', 'SMS reminders'],
      ['Multi-chair', 'Recall automation', 'Intake forms'],
      ['Multi-clinic', 'Insurance integration', 'Custom branding'],
    ),
  },
  {
    slug: 'medical-clinic',
    name: 'Medical Clinic Booking',
    category: 'booking',
    tagline: 'Triage by symptom. Route to the right doctor. Cut reception calls in half.',
    icon: '🏥',
    demoUrl: '/demo/medical-clinic',
    whyNeed:
      'Small clinics have receptionists buried under phone calls. 70% of calls are scheduling that could be done online.',
    whatSolves: [
      'Receptionist overwhelmed, patients on hold',
      'Same-day slots wasted because no one could call in time',
      'No intake form = 10 wasted minutes per visit',
    ],
    moneyImpact:
      'Frees ~15 receptionist hours/week. That is a €400–€600/month labor saving, or redirected to actual patient service.',
    whyPay:
      'One less receptionist shift or one happier receptionist. Either way: real money back every month.',
    features: [
      'Symptom-based doctor routing',
      'Same-day slot release at 8am',
      'Digital intake forms',
      'Multi-language (RO / EN / DE)',
    ],
    plans: standardPlans(
      ['1 doctor', 'Online booking'],
      ['Multi-doctor', 'Triage routing', 'Intake forms'],
      ['Multi-clinic', 'Insurance integration', 'EHR webhook'],
    ),
  },
  {
    slug: 'physio',
    name: 'Physiotherapist Booking',
    category: 'booking',
    tagline: 'Package of 10 sessions sold online. Attendance tracked. Insurance ready.',
    icon: '🧘',
    demoUrl: '/demo/physio',
    whyNeed:
      'Physio practices sell session packages but track them on paper or memory. Packages = cash flow, but only if you sell them.',
    whatSolves: [
      'No online package sale = leaving money on the table',
      'Lost session counts on paper',
      'No insurance claim summary at year-end',
    ],
    moneyImpact:
      'Selling one €250 10-session pack instead of 10 drop-ins lifts per-client revenue by 20%+ and locks in future visits.',
    whyPay: 'One extra package sold per month covers the subscription 8x over.',
    features: [
      'Session package sales',
      'Progress notes per session',
      'Insurance claim export (CSV)',
      'Home exercise PDF email',
    ],
    plans: standardPlans(
      ['1 physio', 'Package sales'],
      ['Multi-therapist', 'Progress notes', 'Insurance export'],
      ['Multi-clinic', 'Custom branding', 'Home exercise library'],
    ),
  },
  {
    slug: 'yoga-studio',
    name: 'Yoga / Pilates Studio',
    category: 'booking',
    tagline: 'Class packs sold online. Drop-in chaos gone.',
    icon: '🧘‍♀️',
    demoUrl: '/demo/yoga-studio',
    whyNeed:
      'Boutique studios run 6–15 classes a week with 10–20 spots each. Managing capacity on paper = overselling and angry students.',
    whatSolves: [
      'Overbooked classes → refund chaos',
      'Students forget which instructor, which room',
      'No easy way to sell 10-class packs',
    ],
    moneyImpact:
      'Selling packages vs drop-ins locks in €200–€400 per student upfront. Better retention, better cash flow.',
    whyPay:
      'Packs become the default selling mode. One new package per week = €100s/month new revenue vs status quo.',
    features: [
      'Class schedule + capacity',
      '10-pack / unlimited monthly sales',
      'Waitlist auto-fill when cancelled',
      'Check-in QR code',
    ],
    plans: standardPlans(
      ['1 studio', '5 classes/week'],
      ['Unlimited classes', 'Packs + memberships'],
      ['Multi-studio', 'Livestream link', 'Custom branding'],
    ),
  },
  {
    slug: 'gym',
    name: 'Gym Membership System',
    category: 'booking',
    tagline: 'Memberships on auto-renew. Entry by QR. Churn down.',
    icon: '🏋️',
    demoUrl: '/demo/gym',
    whyNeed:
      'Small gyms bleed members because they do not proactively contact people who stopped showing up. Churn is the killer.',
    whatSolves: [
      'Silent churn (members pay 2 months without coming, then cancel)',
      'Manual cash/card membership renewals',
      'No trial-to-paid conversion flow',
    ],
    moneyImpact:
      'A "haven\'t seen you in 2 weeks" SMS win-back converts ~15%. At €40/mo membership that is €300–€500/month saved.',
    whyPay: 'Churn prevention alone is worth 3–5x the subscription.',
    features: [
      'Auto-recurring membership billing',
      'QR entry gate',
      'Inactivity win-back SMS',
      'Personal trainer booking add-on',
    ],
    plans: standardPlans(
      ['Up to 100 members', 'QR entry'],
      ['Unlimited members', 'Win-back automation'],
      ['Multi-location', 'PT scheduling', 'Custom app'],
    ),
  },
  {
    slug: 'driving-school',
    name: 'Driving School Booking',
    category: 'booking',
    tagline: 'Students book instructor hours online. Progress tracked. Exam ready.',
    icon: '🚙',
    demoUrl: '/demo/driving-school',
    whyNeed:
      'Driving schools run on instructor-availability puzzles. Coordinating 5 instructors + 30 students on paper is chaos.',
    whatSolves: [
      'Instructor schedule conflicts',
      'Student progress (hours logged, modules done) tracked in notebook',
      'Exam readiness unclear',
    ],
    moneyImpact:
      'Converts ~20% of website visitors who currently just look at the phone number and leave. That is 5–10 extra students/month.',
    whyPay:
      'One extra student enrolled = €500–€800. Pays for 1 year of subscription with ONE conversion.',
    features: [
      'Instructor availability calendar',
      'Student progress dashboard',
      'Package sale (20h, 30h)',
      'Exam readiness checklist',
    ],
    plans: standardPlans(
      ['1 instructor', 'Online booking'],
      ['Up to 10 instructors', 'Progress tracking'],
      ['Multi-city', 'Custom branding', 'Student app'],
    ),
  },
  {
    slug: 'photography',
    name: 'Photography Studio',
    category: 'booking',
    tagline: 'Sessions booked. Deposits taken. Galleries delivered online.',
    icon: '📸',
    demoUrl: '/demo/photography',
    whyNeed:
      'Photographers juggle shoots, edits, and delivery. Without a system, clients disappear between "we should book" and "here is your gallery".',
    whatSolves: [
      'Prospect books by Instagram DM → forgets → ghosts',
      'Deposit collection is awkward ("can you send bank transfer?")',
      'Galleries delivered via WeTransfer = zero branding',
    ],
    moneyImpact:
      'Professional delivery + upsell of prints / albums lifts average client value by 30–50%.',
    whyPay: 'One extra shoot booked per month (€200–€500) covers a year.',
    features: [
      'Online booking + package selection',
      'Stripe / bank deposit',
      'Private client gallery with print shop',
      'Contract e-signature',
    ],
    plans: standardPlans(
      ['10 shoots/mo', 'Online booking', 'Deposits'],
      ['Unlimited', 'Client galleries', 'Print shop'],
      ['Multi-photographer', 'Brand domain', 'Contract signing'],
    ),
  },
  {
    slug: 'veterinarian',
    name: 'Veterinary Clinic Booking',
    category: 'booking',
    tagline: 'Pet profiles. Vaccine reminders. Fewer no-shows.',
    icon: '🐾',
    demoUrl: '/demo/veterinarian',
    whyNeed:
      'Vet clinics lose revenue to missed vaccine follow-ups — most owners just forget their dog is due for a booster.',
    whatSolves: [
      'Vaccine recalls done manually or not at all',
      'Pet history scattered across paper files',
      'Emergency slots unclear to clients',
    ],
    moneyImpact:
      'Automated vaccine reminders recover 30–50% of lapsed clients. For a small clinic, €500–€1,500/mo.',
    whyPay: 'Vaccine recall revenue alone pays for 5+ years of subscription.',
    features: [
      'Pet profile with vaccine dates',
      'Auto reminder 2 weeks before due',
      'Emergency slot reservation',
      'Owner portal for past visits',
    ],
    plans: standardPlans(
      ['1 vet', 'Pet profiles'],
      ['Multi-vet', 'Vaccine automation'],
      ['Multi-clinic', 'Owner app', 'Pharmacy integration'],
    ),
  },

  // ──────────────────────────── CATEGORY 2 — RENTAL ────────────────────────────
  {
    slug: 'car-rental',
    name: 'Car Rental System',
    category: 'rental',
    tagline: 'Full-stack rental: fleet, booking, contracts, WhatsApp notifications.',
    icon: '🚗',
    demoUrl: 'https://sorin-rent-frontend.pages.dev', // TODO: deploy generic demo at cars-demo.myclienta.com (Sorin's URL is a live client site — not for catalog)
    whyNeed:
      'Small rental agencies run on Excel spreadsheets and WhatsApp. Double-bookings happen every month, and contracts are printed from a Word template.',
    whatSolves: [
      'Double-bookings across agents',
      'Paper contracts, lost signatures',
      'No overview of fleet utilization',
      'Manual WhatsApp pickup reminders',
    ],
    moneyImpact:
      'Higher utilization rate (+15–25%) on same fleet = pure profit. On a 10-car fleet at €40/day, that is €1,800–€3,000/month extra.',
    whyPay:
      'Battle-tested platform already running for multiple live car rental clients. Proven product, proven ROI.',
    features: [
      'Fleet availability calendar',
      'Online booking with pricing rules',
      'PDF contract generation + e-signature',
      'WhatsApp pickup/return notifications',
      'Damage report with photos',
      'Revenue reports',
    ],
    plans: standardPlans(
      ['Up to 5 cars', 'Online booking'],
      ['Up to 25 cars', 'Contracts + WhatsApp'],
      ['Unlimited fleet', 'Multi-location', 'API access'],
    ),
  },
  {
    slug: 'spa-hotel',
    name: 'Spa Hotel / Wellness Resort',
    category: 'rental',
    tagline: 'Direct booking for thermal spas, wellness hotels, boutique retreats.',
    icon: '♨️',
    demoUrl: '/demo/spa-hotel',
    whyNeed:
      'Spa hotels list on Booking.com and pay 15% of every reservation. That means a €100 night nets €85. At 500 nights/year that is €7,500 in fees alone. A direct-booking site keeps 100% of the revenue.',
    whatSolves: [
      'Losing 15% to Booking.com on every reservation',
      'No direct customer relationship — Booking owns the email list',
      'Cannot cross-sell spa packages / restaurant at checkout',
      'Weak multi-language handling (EN/MK/GR) on OTA platforms',
    ],
    moneyImpact:
      'Shifting 30% of annual bookings from OTA to direct recovers €3,000–€15,000/year for a small-to-mid spa hotel. Pays for the Business plan forever in month 1.',
    whyPay:
      'Every OTA booking that switches to direct is pure-margin recovered revenue. No spa hotel will argue with "save 15% per booking".',
    features: [
      'Direct booking with room/package selection',
      'Multi-language: EN / MK / GR (cross-border traffic)',
      'iCal sync with Booking.com / Airbnb (avoid double-booking)',
      'Package upsell (spa day, dinner, massage) at checkout',
      'Stripe + IBAN + cash-on-arrival options',
      'Guest review collection + Google Maps integration',
    ],
    plans: standardPlans(
      ['Up to 10 rooms', 'Direct booking'],
      ['Up to 40 rooms', 'Package upsell', 'Multi-language'],
      ['Unlimited', 'Custom domain', 'Channel manager'],
    ),
  },
  {
    slug: 'property-rental',
    name: 'Short-term Property Rental — LifiRent',
    category: 'rental',
    tagline: 'Your own Airbnb-style booking site. No commission to third parties.',
    icon: '🏠',
    demoUrl: 'https://lifirent.com',
    whyNeed:
      'Airbnb takes 15% of every booking. For a property owner with 3+ apartments, that is €500–€2,000/month lost.',
    whatSolves: [
      'Airbnb/Booking.com fees eating margin',
      'No direct relationship with repeat guests',
      'Scattered calendars across platforms',
    ],
    moneyImpact:
      'Owning the booking channel saves 15% on every direct booking. 3 apartments × €1,500/mo × 15% = €675/month saved forever.',
    whyPay: 'Paid back in under 2 weeks of one apartment.',
    features: [
      'Direct booking website',
      'iCal sync with Airbnb / Booking.com',
      'Stripe / bank transfer payments',
      'Automatic check-in instructions',
      'Cleaning team scheduling',
    ],
    plans: standardPlans(
      ['1 property', 'Direct booking'],
      ['Up to 10 properties', 'Multi-calendar sync'],
      ['Unlimited', 'Custom domain', 'Cleaning team portal'],
    ),
  },
  {
    slug: 'event-venue',
    name: 'Event Venue Booking',
    category: 'rental',
    tagline: 'Halls, weddings, conferences — book online with deposit.',
    icon: '🎉',
    demoUrl: '/demo/event-venue',
    whyNeed:
      'Venues quote by email, go back and forth 10 times, then lose clients to a competitor who responded faster.',
    whatSolves: [
      'Slow email quoting loses bookings',
      'No clear availability display',
      'Manual deposit collection',
    ],
    moneyImpact:
      'Instant quote = higher conversion. Converting 1 extra wedding/year = €3,000–€8,000.',
    whyPay: 'One extra booking per year = 10x the subscription.',
    features: [
      'Hall availability calendar',
      'Package selector (wedding / conference / birthday)',
      'Deposit via Stripe',
      '3D virtual tour embed',
    ],
    plans: standardPlans(
      ['1 venue', 'Online booking'],
      ['Multi-hall', 'Package builder', 'Virtual tour'],
      ['Multi-venue', 'Custom branding', 'Vendor marketplace'],
    ),
  },
  {
    slug: 'equipment-rental',
    name: 'Equipment Rental',
    category: 'rental',
    tagline: 'Tools, construction gear, party supplies — rent online, track inventory.',
    icon: '🔨',
    demoUrl: '/demo/equipment-rental',
    whyNeed:
      'Equipment rental shops lose track of which gear is out, when it is due back, and who has it. Lost tools = pure loss.',
    whatSolves: [
      'Lost tools (no tracking)',
      'Late returns not penalized',
      'Manual deposit collection',
    ],
    moneyImpact:
      'Reducing lost tools by 50% alone can save €200–€500/month for a mid-size shop.',
    whyPay: 'Direct savings on lost/late inventory covers cost 3x.',
    features: [
      'Inventory per item with photo',
      'Online booking with deposit',
      'Return reminder SMS',
      'Damage deposit auto-charge',
    ],
    plans: standardPlans(
      ['Up to 50 items', 'Online booking'],
      ['Unlimited items', 'Damage deposit'],
      ['Multi-location', 'Fleet tracking GPS', 'API'],
    ),
  },

  // ──────────────────────────── CATEGORY 3 — HOSPITALITY ────────────────────────────
  {
    slug: 'qr-menu',
    name: 'QR-MENU',
    category: 'hospitality',
    tagline: 'Digital QR menu + scratch-card loyalty game — guests order from their phone and win prizes.',
    icon: '📱',
    demoUrl: 'https://menu.myclienta.com/demo',
    signupUrl: 'https://menu.myclienta.com/signup',
    whyNeed:
      'Paper menus cost money to reprint, can\'t upsell, and give you zero data. A QR menu updates in seconds, shows photos, and the built-in scratch-card game turns first-timers into regulars.',
    whatSolves: [
      'Reprinting menus for every price change',
      'Guests waiting to order — staff overloaded at peak',
      'No reason for guests to come back',
    ],
    moneyImpact:
      'Photo menus lift average order ~10-15%; the scratch-card loyalty game drives return visits. On a venue doing €40k/year that is €4k+ uplift for €19/month.',
    whyPay:
      'Live product with a real venue pilot. €19/month flat, 30-day free trial — the owner can try it risk-free today. Try the customer view live: menu.myclienta.com/?biz=bella-vista-ohrid&table=5 · Pitch page: menu.myclienta.com/pitch · Sign-up: menu.myclienta.com/signup',
    features: [
      'QR menu with photos — instant price updates',
      'Order from the table, kitchen sees it live',
      'Scratch-card loyalty game with prizes',
      'Self-serve signup, 30-day free trial (€19/mo)',
    ],
    // Real live product: ONE price, €19/mo flat (see /pricing + Stripe) — do not
    // use standardPlans() here, its 29/59/99 demo-tier prices contradict the
    // real checkout price and overstate partner commission (queue #10/#15).
    plans: {
      starter: { price: 19, includes: ['QR menu', 'Instant updates'] },
      pro: { price: 19, includes: ['+ Table ordering', '+ Scratch loyalty game'] },
      business: { price: 19, includes: ['Multi-venue', 'White-label', 'Custom domain'] },
    },
  },
  {
    slug: 'coffee-bar',
    name: 'Coffee Bar OS',
    category: 'hospitality',
    tagline: 'Loyalty, mobile order, table QR — everything a café needs in one.',
    icon: '☕',
    demoUrl: '/demo/coffee-bar', // TODO: deploy generic demo at cafe-demo.myclienta.com (maxim.myclienta.com is a live client site — not for catalog)
    whyNeed:
      'Cafés are 90% regulars. Without a loyalty program you are invisible to Starbucks / Costa across the street.',
    whatSolves: [
      'No way to identify/reward regulars',
      'Queue during morning rush = lost sales',
      'No social proof (Instagram promos)',
    ],
    moneyImpact:
      'Loyalty lifts regular-customer frequency ~15%. Average café serves 200 customers/day at €4 → €150/day uplift possible.',
    whyPay:
      'Live in multiple cafés. The Spin Wheel + scratch card drives measurable return visits.',
    features: [
      'LuckyBuzz loyalty (scratch + spin)',
      'Mobile order ahead (skip queue)',
      'Table-side QR ordering',
      'Instagram promo auto-post',
    ],
    plans: standardPlans(
      ['Loyalty program', 'QR menu'],
      ['+ Mobile ordering', '+ Promo engine'],
      ['Multi-location', 'Franchise features', 'Custom app'],
    ),
  },
  {
    slug: 'restaurant',
    name: 'Restaurant OS',
    category: 'hospitality',
    tagline: 'Reservations, menus, voice AI answering calls. Zero paper, full control.',
    icon: '🍽',
    demoUrl: '/demo/restaurant',
    whyNeed:
      'Restaurants are overworked — phone rings during service, reservations scribbled on paper, tables double-booked.',
    whatSolves: [
      'Phone ringing during dinner rush',
      'Scribbled reservation book',
      'No reviews for dishes (no social proof)',
    ],
    moneyImpact:
      'Converting more website visitors into reservations (+20% typical) = €1,500–€3,000/month more revenue for a 40-seat venue.',
    whyPay:
      'Voice AI + online reservations + QR menu = saves 1 staff shift/day. €30–€60/day direct saving.',
    features: [
      'Online table reservation',
      'AI voice receptionist (24/7)',
      'QR table menu + order',
      'Review collection (Google, TripAdvisor)',
    ],
    plans: standardPlans(
      ['Reservations', 'QR menu'],
      ['+ Voice AI', '+ Review engine'],
      ['Multi-venue', 'POS integration', 'Custom branding'],
    ),
  },
  {
    slug: 'pizzeria',
    name: 'Pizzeria Delivery System',
    category: 'hospitality',
    tagline: 'Your own Glovo. Zero commission. Direct delivery.',
    icon: '🍕',
    demoUrl: '/demo/pizzeria',
    whyNeed:
      'Glovo / Wolt take 25–30%. For a pizzeria doing €800/day on delivery, that is €200–€240/day handed over.',
    whatSolves: [
      '25–30% commission to delivery platforms',
      'No customer data (platform owns it)',
      'No direct loyalty / repeat purchase',
    ],
    moneyImpact:
      'Saving even half of platform fees on 50% of orders = €3,000–€4,000/month back to the owner.',
    whyPay: 'Paid back in 3 days of normal operation.',
    features: [
      'Branded ordering website',
      'Live order tracking',
      'Driver dispatch (manual or API)',
      'Loyalty: 10th pizza free',
    ],
    plans: standardPlans(
      ['Online ordering', 'Pickup only'],
      ['+ Delivery', '+ Driver dispatch'],
      ['Multi-location', 'Custom app', 'API integration'],
    ),
  },
  {
    slug: 'bakery',
    name: 'Bakery Pre-Order',
    category: 'hospitality',
    tagline: 'Custom cakes booked online. Pre-orders. Event catering.',
    icon: '🥐',
    demoUrl: '/demo/bakery',
    whyNeed:
      'Bakeries miss orders because cakes are negotiated over the phone ("I need 40 cupcakes for Saturday" → "sorry we were closed"). Offline = lost.',
    whatSolves: [
      'Custom cake orders by phone only',
      'No after-hours order capture',
      'Wedding cake deposits manual',
    ],
    moneyImpact:
      'Capturing 3–5 extra custom orders/month = €300–€1,000 revenue the bakery would have missed entirely.',
    whyPay: 'Any single custom cake order covers the year.',
    features: [
      'Custom cake builder',
      'Pre-order with deposit',
      'Event catering quotes',
      'Daily stock announcement',
    ],
    plans: standardPlans(
      ['Pre-orders', 'Stock announcement'],
      ['+ Custom cake builder', '+ Event catering'],
      ['Multi-location', 'Wholesale portal', 'Custom branding'],
    ),
  },
  {
    slug: 'bar-pub',
    name: 'Bar / Pub Reservation',
    category: 'hospitality',
    tagline: 'Table booking. Event RSVPs. Private party reservations.',
    icon: '🍻',
    demoUrl: '/demo/bar-pub',
    whyNeed:
      'Weekend-evening bars lose revenue because a caller cannot get through, or a group shows up and there is no room.',
    whatSolves: [
      'Phone line busy Friday night = lost groups',
      'No RSVP for live-music events',
      'Private party bookings done on napkins',
    ],
    moneyImpact:
      'Private party bookings (birthdays, corporate) average €500–€2,000. Capturing 1 extra/month = huge.',
    whyPay: 'One captured private party per year covers 5 years of subscription.',
    features: [
      'Table reservation',
      'Event RSVP + ticketing',
      'Private party inquiry form',
      'Birthday bottle service orders',
    ],
    plans: standardPlans(
      ['Table reservation'],
      ['+ Event ticketing', '+ Private parties'],
      ['Multi-venue', 'Custom branding', 'POS integration'],
    ),
  },
  {
    slug: 'winery',
    name: 'Winery Tasting & Direct Sales',
    category: 'hospitality',
    tagline: 'Book wine tastings. Ship wine direct to customer doors. Skip the distributor.',
    icon: '🍷',
    demoUrl: '/demo/winery',
    whyNeed:
      'Small and mid-size wineries (Povardarie, Tikveš, and border regions) sell 70–90% of their wine through distributors who take 30–50% margin. A direct-to-consumer channel plus tasting bookings flips the economics.',
    whatSolves: [
      'Distributor margin eating 30–50% of bottle revenue',
      'Tasting bookings managed by phone / Instagram DM',
      'No wine-club / subscription mechanism for loyal buyers',
      'No language support for Greek / German wine-tourists who visit in summer',
    ],
    moneyImpact:
      'Selling just 500 bottles/year direct at €12 instead of €6 wholesale = €3,000 more margin. Plus tasting revenue: 2 tastings/week × €25 × 4 guests × 40 weeks = €8,000/year. Combined uplift €10k+/year for a boutique winery.',
    whyPay:
      'Any winery doing tastings is already losing bookings to phone-tag. Any winery selling wholesale is leaving margin on the table. Both solved by one tool.',
    features: [
      'Tasting-experience booking (classic / premium / harvest-day)',
      'Direct wine shop with cart + Stripe/IBAN checkout',
      'Wine-club subscription (3 bottles/quarter auto-ship)',
      'Multi-language: EN / MK / GR',
      'Seasonal harvest-events calendar',
      'Wholesale B2B portal (restaurants order pallets)',
    ],
    plans: standardPlans(
      ['Tasting bookings', 'Up to 20 SKUs'],
      ['+ Direct shop', '+ Wine club', 'Multi-language'],
      ['Unlimited', 'B2B wholesale portal', 'Custom domain + branding'],
    ),
  },
  {
    slug: 'ice-cream',
    name: 'Ice Cream / Gelato Shop',
    category: 'hospitality',
    tagline: 'Catering orders, flavor of the week alerts, loyalty.',
    icon: '🍦',
    demoUrl: '/demo/ice-cream',
    whyNeed:
      'Ice cream is impulse + summer-peaked. Shops need tools to extend demand into shoulder seasons and drive repeat visits.',
    whatSolves: [
      'Seasonality — dead periods with no outreach',
      'Catering orders awkward by phone',
      'No loyalty = customers hop shops',
    ],
    moneyImpact:
      'Flavor-of-the-week alert to opted-in customers lifts traffic 5–10%. On summer €1,000 days that is €50–€100/day.',
    whyPay: 'One big catering order per month covers cost.',
    features: [
      'Catering order form',
      'Weekly flavor SMS broadcast',
      'Loyalty (10th cup free)',
      'Birthday cake gelato pre-order',
    ],
    plans: standardPlans(
      ['Loyalty program'],
      ['+ Catering', '+ SMS broadcast'],
      ['Multi-location', 'Wholesale B2B', 'Custom branding'],
    ),
  },

  // ──────────────────────────── CATEGORY 4 — TRADES ────────────────────────────
  {
    slug: 'tile-workers',
    name: 'Tile & Construction Crews',
    category: 'trades',
    tagline: 'Quote requests, photo galleries, job tracking — all in one tradesman CRM.',
    icon: '🧱',
    demoUrl: '/demo/tile-workers', // TODO: deploy generic demo at trades-demo.myclienta.com (old demo URL was client-branded, retired)
    whyNeed:
      'Trades crews get quote requests by WhatsApp, lose half in message scrolling, and never follow up. Every un-quoted lead is cash lost.',
    whatSolves: [
      'Quote requests lost in WhatsApp backlog',
      'No portfolio of past work (photo proof)',
      'Payment by cash — no digital trail',
    ],
    moneyImpact:
      'Quote response time under 2h = 3x higher close rate. One extra job/month = €1,500–€5,000.',
    whyPay: 'One extra job won = 20x annual subscription.',
    features: [
      'Quote request form with photo upload',
      'Job portfolio gallery',
      'Quote-to-invoice pipeline',
      'Client signature on completion',
    ],
    plans: standardPlans(
      ['Quote requests', 'Portfolio'],
      ['+ Invoicing', '+ Crew scheduling'],
      ['Multi-crew', 'Materials ordering', 'Custom branding'],
    ),
  },
  {
    slug: 'plumber',
    name: 'Plumber / Emergency Service',
    category: 'trades',
    tagline: 'Emergency request form → dispatched in minutes. Pre-paid deposits.',
    icon: '🔧',
    demoUrl: '/demo/plumber',
    whyNeed:
      'Emergency plumbers get flooded with calls at 11pm on Sunday and lose leads because they cannot answer all at once.',
    whatSolves: [
      'Missed emergency calls = lost job + bad review',
      'No deposit = flaky customers',
      'No job photos for warranty claims',
    ],
    moneyImpact:
      'One emergency job captured at night = €150–€400. Capturing 3 extra/month = €500–€1,200.',
    whyPay: 'Emergency-deposit enforcement alone prevents €200–€500/month of ghosted jobs.',
    features: [
      'Emergency form with priority routing',
      'Deposit required for callout',
      'Before/after photo log',
      'Warranty record per job',
    ],
    plans: standardPlans(
      ['Quote requests'],
      ['+ Emergency routing', '+ Deposits'],
      ['Multi-plumber', 'Parts catalog', 'Custom branding'],
    ),
  },
  {
    slug: 'electrician',
    name: 'Electrician / Installation',
    category: 'trades',
    tagline: 'Panel upgrade quotes. Certificate generation. Safety log.',
    icon: '⚡',
    demoUrl: '/demo/electrician',
    whyNeed:
      'Electricians deal with inspection certificates that clients lose and then call the electrician again for a reprint — free labor.',
    whatSolves: [
      'Paper certificates lost by clients',
      'No digital safety log for compliance',
      'Quoting by phone = slow + forgetful',
    ],
    moneyImpact:
      'Digital cert archive saves ~2h/week of repeat requests + lifts professional appearance = more referrals.',
    whyPay: 'Professional image converts more quotes. One extra big install/month pays for the year.',
    features: [
      'Installation quote builder',
      'PDF safety certificates (branded)',
      'Digital safety log',
      'Before/after photos',
    ],
    plans: standardPlans(
      ['Quotes + certificates'],
      ['+ Safety log', '+ Photo gallery'],
      ['Multi-technician', 'Parts catalog', 'API to accounting'],
    ),
  },
  {
    slug: 'painter-renovation',
    name: 'Painter / Renovation',
    category: 'trades',
    tagline: 'Visual quotes from photos. Before/after portfolio. Social proof.',
    icon: '🎨',
    demoUrl: '/demo/painter-renovation',
    whyNeed:
      'Renovators lose clients because quotes take 3 days (they drive to see the site) while the client just wants a ballpark.',
    whatSolves: [
      'Slow quote turnaround loses clients',
      'No portfolio to show credibility',
      'Material costs quoted roughly, margin squeezed',
    ],
    moneyImpact:
      'Instant photo-based rough-quote converts 2x more leads. €2,000–€5,000 additional jobs/month realistic.',
    whyPay: 'One medium renovation job pays for multiple years.',
    features: [
      'Photo-upload quote request',
      'Before/after portfolio',
      'Material cost calculator',
      'Milestone-based invoicing',
    ],
    plans: standardPlans(
      ['Quotes + portfolio'],
      ['+ Material calculator', '+ Milestones'],
      ['Multi-crew', 'Project management', 'Custom branding'],
    ),
  },
  {
    slug: 'cleaning-service',
    name: 'Cleaning Service',
    category: 'trades',
    tagline: 'Recurring bookings. Staff rota. Quality photo check.',
    icon: '🧹',
    demoUrl: '/demo/cleaning-service',
    whyNeed:
      'Cleaning services are 80% recurring jobs. Without a system, owners spend 10h/week on scheduling WhatsApps.',
    whatSolves: [
      'Hours wasted on scheduling by hand',
      'No photo proof of work done',
      'Staff no-shows unpunished',
    ],
    moneyImpact:
      'Automation saves 8–15h/week of the owner — value €400–€800/month even at local wages.',
    whyPay: 'Owner gets their weekend back. That is worth more than the subscription.',
    features: [
      'Recurring booking engine',
      'Staff assignment + rota',
      'Photo quality check',
      'Customer rating per job',
    ],
    plans: standardPlans(
      ['Up to 20 bookings/mo'],
      ['Unlimited', '+ Staff rota'],
      ['Multi-team', 'Franchise ready', 'Custom branding'],
    ),
  },
  {
    slug: 'gardener',
    name: 'Gardener / Landscaping',
    category: 'trades',
    tagline: 'Seasonal contracts. Photo portfolio. Auto-invoice.',
    icon: '🌿',
    demoUrl: '/demo/gardener',
    whyNeed:
      'Landscapers have seasonal recurring contracts (weekly lawn, monthly hedge) that get lost to manual spreadsheets.',
    whatSolves: [
      'Seasonal contracts tracked on paper',
      'No visual portfolio = commodity pricing',
      'Invoicing delayed 2 weeks',
    ],
    moneyImpact:
      'Instant invoice-on-completion cuts collection time from 2 weeks to same-day. Cash flow boost.',
    whyPay: 'Time saved + faster cash = worth 3x subscription.',
    features: [
      'Recurring service contracts',
      'Photo portfolio',
      'Auto-invoice on job done',
      'Seasonal marketing SMS',
    ],
    plans: standardPlans(
      ['Recurring bookings'],
      ['+ Portfolio', '+ Auto-invoice'],
      ['Multi-crew', 'Materials catalog', 'Custom branding'],
    ),
  },
  {
    slug: 'moving-company',
    name: 'Moving / Relocation Company',
    category: 'trades',
    tagline: 'Video-survey quotes. Crew scheduling. Claim forms built in.',
    icon: '📦',
    demoUrl: '/demo/moving-company',
    whyNeed:
      'Movers lose leads because quotes require a home visit. A 5-minute video survey closes the gap.',
    whatSolves: [
      'Slow quote process loses leads',
      'Crew scheduling in spreadsheets',
      'Damage claims on paper',
    ],
    moneyImpact:
      'Video-survey closing rate: 45% vs 20% on email-only. Doubles closed jobs.',
    whyPay: 'One captured move (€500+) per month covers 1+ year.',
    features: [
      'Video upload quote form',
      'Crew assignment calendar',
      'Inventory checklist + photos',
      'Damage claim portal',
    ],
    plans: standardPlans(
      ['Quote form'],
      ['+ Crew scheduling', '+ Inventory'],
      ['Multi-truck', 'Franchise', 'API'],
    ),
  },

  // ──────────────────────────── CATEGORY 5 — VOICE AI ────────────────────────────
  {
    slug: 'voice-restaurant',
    name: 'Voice AI — Restaurant',
    category: 'voice',
    tagline: 'AI answers the phone, books tables, takes orders. 24/7.',
    icon: '🍝',
    demoUrl: 'https://voice-demo-865.pages.dev',
    whyNeed:
      'Restaurants miss 20–30% of reservation calls because staff is serving tables. Every missed call = lost booking + bad reputation.',
    whatSolves: [
      '30% of calls go unanswered during service',
      'Staff interrupted mid-service to answer phone',
      'After-hours calls = lost reservations',
    ],
    moneyImpact:
      'Capturing 3 extra reservations/day at €30 avg ticket = €90/day = €2,700/month.',
    whyPay:
      'Voice AI pays for itself after 1 captured reservation per month.',
    features: [
      'Multi-language: RO / EN / DE / MK',
      'Table reservation in calendar',
      'Menu read-out + recommendations',
      'Call transcript to SMS',
    ],
    plans: standardPlans(
      ['500 min/mo', '1 language'],
      ['2000 min/mo', 'Multi-language'],
      ['Unlimited', 'Custom voice', 'CRM integration'],
    ),
  },
  {
    slug: 'voice-clinic',
    name: 'Voice AI — Medical Clinic',
    category: 'voice',
    tagline: 'AI triages patients by symptom, books the right doctor, handles repeats.',
    icon: '🏨',
    demoUrl: 'https://voice-demo-865.pages.dev',
    whyNeed:
      'Clinic receptionists are buried under phone calls, 70% of which are scheduling.',
    whatSolves: [
      'Receptionist overwhelmed',
      'Calls drop during lunch hour',
      'No after-hours capture',
    ],
    moneyImpact:
      'Frees 15–20 receptionist hours/week = €600–€1,000/month labor.',
    whyPay:
      'Either save a shift or redirect receptionist to better patient service.',
    features: [
      'Symptom-based triage',
      'Appointment booking',
      'Prescription refill requests',
      'Call log to EHR',
    ],
    plans: standardPlans(
      ['500 min/mo'],
      ['2000 min/mo', 'EHR webhook'],
      ['Unlimited', 'Custom voice', 'Insurance verification'],
    ),
  },
  {
    slug: 'voice-hotel',
    name: 'Voice AI — Hotel Concierge',
    category: 'voice',
    tagline: 'AI answers guest questions, books spa, orders room service.',
    icon: '🛎',
    demoUrl: 'https://voice-demo-865.pages.dev',
    whyNeed:
      'Night staff at small hotels is stretched thin. Guests calling at 11pm for towels or wake-up calls go unanswered.',
    whatSolves: [
      'Understaffed night shift',
      'Wake-up call errors',
      'No recorded guest preferences',
    ],
    moneyImpact:
      'Upsells (spa, late checkout, airport transfer) captured automatically lift revenue 5–10%.',
    whyPay: 'Upsell revenue alone covers it.',
    features: [
      'Guest concierge Q&A',
      'Wake-up call scheduling',
      'Spa / restaurant upsell',
      'Multi-language',
    ],
    plans: standardPlans(
      ['Up to 20 rooms'],
      ['Up to 100 rooms', 'Upsell engine'],
      ['Unlimited', 'Custom voice', 'PMS integration'],
    ),
  },
  {
    slug: 'voice-generic',
    name: 'Voice AI — General Business',
    category: 'voice',
    tagline: 'One AI receptionist. Any business. Never misses a call.',
    icon: '📞',
    demoUrl: 'https://voice-demo-865.pages.dev',
    whyNeed:
      'Any small business loses 20–40% of calls. Missed calls = missed revenue.',
    whatSolves: [
      'Missed calls during work hours',
      'After-hours inquiries lost',
      'No CRM capture',
    ],
    moneyImpact:
      'A single captured lead/week usually pays for the entire year.',
    whyPay: 'Cheapest employee you will ever hire.',
    features: [
      'Caller intent detection',
      'Appointment booking',
      'Lead capture + SMS follow-up',
      'Call transcripts',
    ],
    plans: standardPlans(
      ['500 min/mo'],
      ['2000 min/mo'],
      ['Unlimited', 'Custom branding', 'CRM integration'],
    ),
  },

  // ──────────────────────────── CATEGORY 6 — LOYALTY ────────────────────────────
  {
    slug: 'loyalty-cafe',
    name: 'Café Loyalty — LuckyBuzz',
    category: 'loyalty',
    tagline: 'Scratch cards + spin wheels. Regulars come back 25% more.',
    icon: '🎰',
    demoUrl: 'https://luckybuzz.pages.dev',
    whyNeed:
      'Punch cards are paper-era. They get lost, faked, and have no data. LuckyBuzz makes loyalty visual, viral, and data-driven.',
    whatSolves: [
      'Lost punch cards',
      'No data on who regulars are',
      'No viral Instagram mechanic',
    ],
    moneyImpact:
      'Visit frequency lift of ~20%. On a café doing €50k/year that is €10k uplift.',
    whyPay: 'Live in multiple venues across Balkans. Proven retention lift.',
    features: [
      'Scratch card wins',
      'Spin wheel daily',
      'Stamp card alternative',
      'Customer data export',
    ],
    plans: standardPlans(
      ['Scratch cards'],
      ['+ Spin wheel', '+ SMS blast'],
      ['Multi-location', 'White-label', 'API'],
    ),
  },
  {
    slug: 'loyalty-restaurant',
    name: 'Restaurant Loyalty',
    category: 'loyalty',
    tagline: 'Every 10th visit free. Birthday dessert auto-sent. No apps to install.',
    icon: '🍷',
    demoUrl: 'https://luckybuzz.pages.dev',
    whyNeed:
      'Restaurants live off regulars but never capture them. A tap-in loyalty program turns one-timers into 3-timers.',
    whatSolves: [
      'No customer data',
      'No birthday marketing',
      'No post-visit follow-up',
    ],
    moneyImpact:
      'Birthday auto-reminder fills 5–10 extra tables/month. €300–€700 uplift.',
    whyPay: 'Birthday revenue alone covers it.',
    features: [
      'Visit-count loyalty',
      'Birthday auto-SMS',
      'Tiered rewards (bronze / silver / gold)',
      'Referral bonuses',
    ],
    plans: standardPlans(
      ['Visit loyalty'],
      ['+ Birthday engine', '+ Tiers'],
      ['Multi-venue', 'Custom rewards', 'API'],
    ),
  },
  {
    slug: 'loyalty-digital-card',
    name: 'Digital Loyalty Card — Phone, Not Plastic',
    category: 'loyalty',
    tagline: 'Kill the plastic punch card. Customer scans QR → points auto-added → balance on their phone.',
    icon: '📱',
    demoUrl: 'https://luckybuzz.pages.dev', // TODO: build standalone demo — phone mockup + QR flow
    whyNeed:
      'Many small shops, pharmacies, fuel stations, and bakeries in Macedonia still use a plastic fiscal / punch card ("get 10 stamps = free item"). Customers lose the cards, forget them at home, or carry 8 different plastic cards. The result: 60–70% of issued cards are never redeemed.',
    whatSolves: [
      'Lost / forgotten plastic cards = broken loyalty loop',
      'Customers carry 8 plastic cards in a wallet (annoying)',
      'No data on who visits, how often, what they buy',
      'Paper/plastic printing costs every 6 months',
    ],
    moneyImpact:
      'Typical fiscal-card redemption rate: ~30%. Digital-card redemption rate on phone: ~75%. More redemptions = more repeat visits. For a busy fuel station or bakery with 200 daily customers, the traffic lift is worth €500–€2,000/month.',
    whyPay:
      'They already budget for printing plastic cards. Replacing that with a digital tool that also captures customer data is a no-brainer sale — especially to chains with 3+ locations.',
    features: [
      'QR code printed at POS (or on receipt)',
      'Customer scans → phone wallet adds points (no app install)',
      'Auto-notification at redemption threshold ("you earned a free coffee!")',
      'Birthday / anniversary auto-offers',
      'Staff dashboard: daily redemptions, top customers',
      'Multi-location support for chains (e.g., bakery with 3 shops)',
    ],
    plans: standardPlans(
      ['1 location', 'Basic points'],
      ['Up to 5 locations', '+ Birthday offers', '+ Customer data export'],
      ['Unlimited locations', 'API to POS', 'Custom branding + app wallet'],
    ),
  },
  {
    slug: 'loyalty-retail',
    name: 'Retail Scratch & Win',
    category: 'loyalty',
    tagline: 'Gamified discount engine. Shoppers open it daily.',
    icon: '🎁',
    demoUrl: 'https://luckybuzz.pages.dev',
    whyNeed:
      'Retail shops bleed traffic to online. A gamified discount engine is the one thing a physical shop can do that Amazon cannot.',
    whatSolves: [
      'Flat email blasts ignored',
      'No habit of daily store-check',
      'No FOMO on promotions',
    ],
    moneyImpact:
      'Daily-open rate of ~40% on a game-based push vs ~5% for email.',
    whyPay: 'Same budget, 8x attention.',
    features: [
      'Daily scratch card',
      'Tiered discount wheel',
      'Expiring coupon codes',
      'Heatmap of engagement',
    ],
    plans: standardPlans(
      ['Scratch cards'],
      ['+ Wheel', '+ SMS'],
      ['Multi-store', 'White-label', 'API'],
    ),
  },

  // ──────────────────────────── CATEGORY 7 — E-COMMERCE ────────────────────────────
  {
    slug: 'flower-shop',
    name: 'Flower Shop',
    category: 'ecommerce',
    tagline: 'Same-day delivery orders. Subscription bouquets. Events.',
    icon: '💐',
    demoUrl: '/demo/flower-shop',
    whyNeed:
      'Flower shops miss same-day online orders because they have no proper website. Competitors with Shopify take the business.',
    whatSolves: [
      'No online store = invisible on Google',
      'No delivery slot coordination',
      'No subscription (weekly bouquet) revenue',
    ],
    moneyImpact:
      'Online orders: 20–40% of revenue for modern flower shops. Zero today = huge upside.',
    whyPay: 'One extra online order per day covers the year 2x over.',
    features: [
      'Catalog + photos',
      'Same-day delivery slot',
      'Weekly subscription bouquet',
      'Event flowers quote',
    ],
    plans: standardPlans(
      ['Up to 50 products'],
      ['+ Subscriptions', '+ Events'],
      ['Unlimited', 'Custom branding', 'Multi-store'],
    ),
  },
  {
    slug: 'clothing-boutique',
    name: 'Clothing Boutique',
    category: 'ecommerce',
    tagline: 'Instagram shop → website → checkout. Local delivery.',
    icon: '👗',
    demoUrl: '/demo/clothing-boutique',
    whyNeed:
      'Boutiques sell on Instagram but lose sales at the "how do I pay?" step. Comments are a terrible checkout.',
    whatSolves: [
      'Instagram DM sales chaos',
      'No stock sync across posts',
      'Local delivery uncoordinated',
    ],
    moneyImpact:
      'Proper checkout lifts Instagram-sourced conversion 2–3x.',
    whyPay: 'Less time in DMs, more time sourcing stock.',
    features: [
      'Shopify-style catalog',
      'Instagram product tagging',
      'Local delivery slots',
      'Size / fit Q&A chatbot',
    ],
    plans: standardPlans(
      ['Up to 50 products'],
      ['Unlimited', '+ IG sync'],
      ['Multi-store', 'POS integration', 'Custom branding'],
    ),
  },
  {
    slug: 'pharmacy',
    name: 'Local Pharmacy',
    category: 'ecommerce',
    tagline: 'Online pre-order for pickup. Prescription reminders. Home delivery.',
    icon: '💊',
    demoUrl: '/demo/pharmacy',
    whyNeed:
      'Pharmacies face competition from online pharmacies. Pre-order-for-pickup keeps clients loyal to local.',
    whatSolves: [
      'Pharmacy queues drive away customers',
      'No prescription refill reminder',
      'No home-delivery capability',
    ],
    moneyImpact:
      'Retention vs online pharmacies: saves €500–€2,000/mo lost to Dr.Max / Catena / DocMorris.',
    whyPay: 'Protects existing revenue, not just grows new.',
    features: [
      'Pre-order pickup',
      'Prescription refill reminder',
      'Home delivery for seniors',
      'Consultation booking',
    ],
    plans: standardPlans(
      ['Pre-order'],
      ['+ Refill reminders', '+ Delivery'],
      ['Multi-branch', 'ERP integration', 'Custom app'],
    ),
  },
  {
    slug: 'pet-shop',
    name: 'Pet Shop',
    category: 'ecommerce',
    tagline: 'Pet food subscription. Grooming booking. Vet directory.',
    icon: '🐕',
    demoUrl: '/demo/pet-shop',
    whyNeed:
      'Pet shops get beaten by Zooplus on convenience. Local grooming + subscription food levels the field.',
    whatSolves: [
      'Customers order dog food from Zooplus',
      'Grooming booking by phone only',
      'No cross-sell to vet partners',
    ],
    moneyImpact:
      'Subscription food (€40/mo × 30 subscribers) = €1,200/month recurring.',
    whyPay: 'Subscription model changes the business.',
    features: [
      'Pet food subscription',
      'Grooming booking',
      'Vet referral directory',
      'Loyalty (10 bags = 1 free)',
    ],
    plans: standardPlans(
      ['Catalog + booking'],
      ['+ Subscriptions', '+ Loyalty'],
      ['Multi-store', 'Vet directory', 'Custom branding'],
    ),
  },
  {
    slug: 'jewelry-store',
    name: 'Jewelry Store',
    category: 'ecommerce',
    tagline: 'Custom engagement quotes. Repair tracking. Appraisals.',
    icon: '💍',
    demoUrl: '/demo/jewelry-store',
    whyNeed:
      'Jewelry is a high-trust, custom-per-client sale. Without a quote/appraisal portal, everything happens via email at snail pace.',
    whatSolves: [
      'Slow engagement-ring quotes',
      'Repair status by phone = 10 calls/day',
      'No appraisal digital archive',
    ],
    moneyImpact:
      'Faster custom-quote turnaround closes 2x more engagement sales. Avg ticket: €1,500.',
    whyPay: 'One ring sale = 30+ years of subscription.',
    features: [
      'Custom ring builder',
      'Repair status portal',
      'Appraisal PDF delivery',
      'Consultation booking',
    ],
    plans: standardPlans(
      ['Catalog'],
      ['+ Ring builder', '+ Repair portal'],
      ['Multi-store', 'Appraisals', 'Custom branding'],
    ),
  },

  // ──────────────────────────── CATEGORY 8 — PROFESSIONAL ────────────────────────────
  {
    slug: 'lawyer',
    name: 'Lawyer / Law Firm',
    category: 'professional',
    tagline: 'Case intake form. Consultation booking. Document request portal.',
    icon: '⚖️',
    demoUrl: '/demo/lawyer',
    whyNeed:
      'Lawyers qualify leads manually over 3–5 email back-and-forths before a consultation. A proper intake form saves 2h per lead.',
    whatSolves: [
      'Unqualified leads wasting consult time',
      'Document collection by email chaos',
      'No professional digital face',
    ],
    moneyImpact:
      'Saving 2h/lead × 10 leads/week × €80/h = €1,600/month. Direct.',
    whyPay: 'Time = money. Literally.',
    features: [
      'Case intake qualification form',
      'Consultation booking + deposit',
      'Document upload portal',
      'Conflict-check intake',
    ],
    plans: standardPlans(
      ['Intake form + booking'],
      ['+ Document portal'],
      ['Multi-lawyer', 'CRM integration', 'Custom branding'],
    ),
  },
  {
    slug: 'accountant',
    name: 'Accountant',
    category: 'professional',
    tagline: 'Document upload. Tax deadline reminders. Annual package pricing.',
    icon: '📊',
    demoUrl: '/demo/accountant',
    whyNeed:
      'Accountants spend 50% of their time chasing clients for receipts and documents. A portal ends the email war.',
    whatSolves: [
      'Receipt collection nightmare',
      'Missed tax deadlines = penalty claims',
      'Vague pricing drives clients away',
    ],
    moneyImpact:
      'Recover 10h/week of lost admin. At €50/h that is €2,000/month.',
    whyPay: 'Hours back alone justify it 40x over.',
    features: [
      'Document upload portal',
      'Tax deadline automation',
      'Package pricing (annual / monthly)',
      'Quarterly report auto-email',
    ],
    plans: standardPlans(
      ['Document portal'],
      ['+ Deadline automation'],
      ['Multi-accountant', 'ERP integration', 'Custom branding'],
    ),
  },
  {
    slug: 'notary',
    name: 'Notary Office',
    category: 'professional',
    tagline: 'Appointment types (contracts, inheritance, power-of-attorney). Pre-upload docs.',
    icon: '📜',
    demoUrl: '/demo/notary',
    whyNeed:
      'Notary appointments fail when clients forget required documents. Pre-upload checklists fix this.',
    whatSolves: [
      'Wasted slots on incomplete appointments',
      'Phone calls for "what do I need to bring"',
      'No digital receipt of meeting',
    ],
    moneyImpact:
      'Even 1 saved slot/day (€50–€150) = €1,000–€3,000/month.',
    whyPay: 'Zero-effort productivity bump.',
    features: [
      'Service-type picker',
      'Document checklist per service',
      'Pre-upload portal',
      'Digital meeting receipt',
    ],
    plans: standardPlans(
      ['Booking + checklist'],
      ['+ Pre-upload', '+ Receipts'],
      ['Multi-notary', 'API to court', 'Custom branding'],
    ),
  },
  {
    slug: 'insurance-agent',
    name: 'Insurance Agent',
    category: 'professional',
    tagline: 'Quote comparison widget. Renewal alerts. Claim forms.',
    icon: '🛡',
    demoUrl: '/demo/insurance-agent',
    whyNeed:
      'Insurance agents lose renewals because they forget to follow up 30 days before expiry. Automation captures them all.',
    whatSolves: [
      'Silent churn on renewals',
      'No quote comparison widget',
      'Claims handled by paper',
    ],
    moneyImpact:
      'Renewal follow-up automation recovers 20% of silent-churners. For 200 policies × €300/year × 20% = €12,000/year.',
    whyPay: 'Pure renewal revenue protection.',
    features: [
      'Auto-renewal SMS 30d before',
      'Quote comparison (3 carriers)',
      'Claim form digital intake',
      'Policy archive per client',
    ],
    plans: standardPlans(
      ['Renewal automation'],
      ['+ Quote widget', '+ Claims'],
      ['Multi-agent', 'Carrier API', 'Custom branding'],
    ),
  },
  {
    slug: 'marketing-agency',
    name: 'Marketing Agency',
    category: 'professional',
    tagline: 'Client onboarding portal. Retainer billing. Reporting dashboards.',
    icon: '📣',
    demoUrl: '/demo/marketing-agency',
    whyNeed:
      'Marketing agencies have ~5 clients and drown in reporting. A dashboard per client = half the time to deliver value.',
    whatSolves: [
      'PowerPoint reporting burning 10h/week',
      'Scope creep from unclear onboarding',
      'Retainer billing done manually',
    ],
    moneyImpact:
      'Automating reports saves 40h/month. At €50/h = €2,000/month.',
    whyPay: 'Agency owner profit margin jumps.',
    features: [
      'Client onboarding forms',
      'Retainer recurring billing',
      'Google Ads / GA4 dashboards',
      'Contract e-signature',
    ],
    plans: standardPlans(
      ['5 clients'],
      ['20 clients', '+ Dashboards'],
      ['Unlimited', 'White-label', 'API'],
    ),
  },

  // ──────────────────────────── CATEGORY 9 — CUSTOM ────────────────────────────
  {
    slug: 'custom',
    name: 'Custom Build',
    category: 'custom',
    tagline: 'Do not see your business? We build a custom solution. Quote in 24h.',
    icon: '✨',
    demoUrl: '/demo/custom',
    whyNeed:
      'Every business has unique workflows. When our 50 pre-built templates do not fit, we can build a custom app from scratch in 1–4 weeks.',
    whatSolves: [
      'Niche business with no off-the-shelf tool',
      'Unique workflow not covered by catalog',
      'Legacy system replacement',
    ],
    moneyImpact:
      'Custom = priced per project. Typical build: €500–€3,000 one-time + €49–€199/mo hosting. Referrer earns €100–€500 instant commission plus 30% of monthly for 6 months.',
    whyPay:
      'When there is no off-the-shelf option, custom is the only option. Businesses will pay for what solves their specific problem.',
    features: [
      'Discovery call (free, 30 min)',
      'Fixed-price quote in 24h',
      'Delivered in 1–4 weeks',
      'Hosted + maintained by us',
    ],
    plans: standardPlans(
      ['Starter build', 'Basic hosting'],
      ['Pro build', 'Priority support'],
      ['Enterprise', 'SLA + SSO', 'Dedicated success manager'],
    ),
  },
]

// Auto-wire demoUrl for any product whose slug has a template config in
// ../demos/configs.ts. Keeps the demo URLs in one place: the slug. If a
// product already has an explicit demoUrl (e.g. LifiRent's real site, or a
// live external demo like luckybuzz), we leave it alone.
import { BOOKING_CONFIGS, ORDER_CONFIGS, TRADES_CONFIGS } from '../demos/configs'
const TEMPLATE_SLUGS = new Set<string>([
  ...Object.keys(BOOKING_CONFIGS),
  ...Object.keys(ORDER_CONFIGS),
  ...Object.keys(TRADES_CONFIGS),
  'spa-hotel',
  'restaurant',
  'winery',
])
for (const p of PRODUCTS) {
  if (!p.demoUrl && TEMPLATE_SLUGS.has(p.slug)) {
    p.demoUrl = `/demo/${p.slug}`
  }
}

export const getCategory = (slug: string) => CATEGORIES.find((c) => c.slug === slug)
export const getProduct = (slug: string) => PRODUCTS.find((p) => p.slug === slug)
export const getProductsByCategory = (slug: string) =>
  PRODUCTS.filter((p) => p.category === slug)

// menu.myclienta.com (QR-MENU's live demo) supports ?lang=mk — without it a
// Macedonian visitor who clicked "Try live demo" from the MK catalog page
// lands on an all-English demo (queue #5).
export function demoHref(product: { slug: string; demoUrl?: string | null }, lang: string) {
  if (!product.demoUrl) return undefined
  return product.slug === 'qr-menu' && lang === 'mk' ? `${product.demoUrl}?lang=mk` : product.demoUrl
}

// Commission helpers — same across all products, per Djoko's spec:
//   €30 one-time setup (all to referrer) + 30% of monthly × 6 months
export const COMMISSION = {
  setupFee: 30, // €, one-time, paid by business owner, goes 100% to referrer
  recurringPercent: 30, // % of monthly subscription
  recurringMonths: 6,
}

export function calcCommission(monthlyPrice: number) {
  const setup = COMMISSION.setupFee
  const monthly = Math.round(monthlyPrice * (COMMISSION.recurringPercent / 100) * 100) / 100
  const total6m = Math.round((setup + monthly * COMMISSION.recurringMonths) * 100) / 100
  return { setup, monthly, months: COMMISSION.recurringMonths, total6m }
}

// Optional one-time add-on: custom domain + branded setup.
// Business-tier only. Business owner pays €149, referrer earns 30% (€44.70).
// Remaining €104.30 covers domain registration + our setup time.
export const CUSTOM_DOMAIN_ADDON = {
  price: 149,
  referrerPercent: 30,
  availableOn: ['business'] as const,
}

export function calcDomainCommission() {
  const price = CUSTOM_DOMAIN_ADDON.price
  const referrer = Math.round(price * (CUSTOM_DOMAIN_ADDON.referrerPercent / 100) * 100) / 100
  return { price, referrer, owner: Math.round((price - referrer) * 100) / 100 }
}
